import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// Language type
export type Language = 'de' | 'en';

// Context value interface
export interface LanguageContextValue {
  language: Language;
  setLanguage: (lang: Language) => void;
  toggleLanguage: () => void;
}

// Create context
const LanguageContext = createContext<LanguageContextValue | undefined>(undefined);

// LocalStorage key
const STORAGE_KEY = 'sovereign-cloud-language';

// Provider props
interface LanguageProviderProps {
  children: ReactNode;
}

// Provider component
export function LanguageProvider({ children }: LanguageProviderProps) {
  // Initialize from localStorage or default to 'de'
  const [language, setLanguageState] = useState<Language>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === 'de' || stored === 'en') {
        return stored;
      }
    } catch (error) {
      console.warn('Failed to read language from localStorage:', error);
    }
    return 'de'; // Default to German
  });

  // Update localStorage when language changes
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, language);
      // Update html lang attribute for accessibility
      document.documentElement.lang = language;
    } catch (error) {
      console.warn('Failed to save language to localStorage:', error);
    }
  }, [language]);

  // Set language function
  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
  };

  // Toggle between languages
  const toggleLanguage = () => {
    setLanguageState((prev) => (prev === 'de' ? 'en' : 'de'));
  };

  const value: LanguageContextValue = {
    language,
    setLanguage,
    toggleLanguage,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

// Custom hook to use language context
export function useLanguageContext(): LanguageContextValue {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguageContext must be used within a LanguageProvider');
  }
  return context;
}
